import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './FooterSettings.css';
import { FOOTER_SOCIAL_PLATFORMS } from '../../constants/footerSocialPlatforms';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const emptyFooter = {
  brandName: '',
  firmDescription: '',
  focusHeading: '',
  focusItems: ['', '', ''],
  contactHeading: '',
  address: '',
  phone: '',
  email: '',
  copyrightText: '',
  socialLinks: [],
};

function buildSocialLinksFromResponse(data) {
  if (Array.isArray(data.socialLinks) && data.socialLinks.length > 0) {
    return data.socialLinks.map((item) => ({
      platform: String(item.platform || 'linkedin').trim().toLowerCase() || 'linkedin',
      url: String(item.url || ''),
    }));
  }
  const legacy = [];
  if (data.linkedinUrl && String(data.linkedinUrl).trim()) {
    legacy.push({ platform: 'linkedin', url: String(data.linkedinUrl).trim() });
  }
  if (data.facebookUrl && String(data.facebookUrl).trim()) {
    legacy.push({ platform: 'facebook', url: String(data.facebookUrl).trim() });
  }
  return legacy;
}

function FooterSettings() {
  const [footer, setFooter] = useState(emptyFooter);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchFooter = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/footer`);
        const data = response.data || {};
        setFooter({
          brandName: data.brandName ?? '',
          firmDescription: data.firmDescription ?? '',
          focusHeading: data.focusHeading ?? '',
          focusItems: Array.isArray(data.focusItems)
            ? [...data.focusItems, '', '', ''].slice(0, 3)
            : ['', '', ''],
          contactHeading: data.contactHeading ?? '',
          address: data.address ?? '',
          phone: data.phone ?? '',
          email: data.email ?? '',
          copyrightText: data.copyrightText ?? '',
          socialLinks: buildSocialLinksFromResponse(data),
        });
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFooter();
  }, []);

  const handleChange = (field, value) => {
    setFooter((prev) => ({ ...prev, [field]: value }));
  };

  const handleFocusItemChange = (index, value) => {
    setFooter((prev) => {
      const next = [...(prev.focusItems || ['', '', ''])];
      next[index] = value;
      return { ...prev, focusItems: next };
    });
  };

  const handleSocialChange = (index, field, value) => {
    setFooter((prev) => {
      const next = [...(prev.socialLinks || [])];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, socialLinks: next };
    });
  };

  const addSocialLink = () => {
    setFooter((prev) => ({
      ...prev,
      socialLinks: [
        ...(prev.socialLinks || []),
        { platform: 'linkedin', url: '' },
      ],
    }));
  };

  const removeSocialLink = (index) => {
    setFooter((prev) => ({
      ...prev,
      socialLinks: (prev.socialLinks || []).filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage('');

    const payload = {
      ...footer,
      socialLinks: (footer.socialLinks || []).map((row) => ({
        platform: String(row.platform || '').trim().toLowerCase(),
        url: String(row.url || '').trim(),
      })),
    };

    try {
      await axios.put(`${API_BASE_URL}/api/footer`, payload);
      setMessage('Footer updated successfully.');
    } catch (err) {
      console.error(err);
      setMessage('Unable to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <p className="footer-settings-loading">Loading footer…</p>;
  }

  const focusItems = footer.focusItems || ['', '', ''];
  const socialLinks = footer.socialLinks || [];

  return (
    <div className="footer-settings">
      <div className="footer-settings-header">
        <div>
          <h2 className="footer-settings-title">Footer</h2>
          <p className="footer-settings-subtitle">
            Edit brand, description, focus list, contact details, copyright, and social links.
          </p>
        </div>
        <button
          type="button"
          className="footer-settings-save-btn"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      <div className="footer-settings-grid">
        <div className="footer-settings-field">
          <label className="footer-settings-label">
            Brand name
            <input
              type="text"
              className="footer-settings-input"
              value={footer.brandName}
              onChange={(e) => handleChange('brandName', e.target.value)}
              placeholder="Whales Advisory"
            />
          </label>
        </div>

        <div className="footer-settings-field footer-settings-field-wide">
          <label className="footer-settings-label">
            Firm description
            <textarea
              className="footer-settings-textarea"
              rows={2}
              value={footer.firmDescription}
              onChange={(e) => handleChange('firmDescription', e.target.value)}
              placeholder="Delivering refined, solution-driven legal support..."
            />
          </label>
        </div>

        <div className="footer-settings-field footer-settings-field-wide">
          <h3 className="footer-settings-section-title">Our Focus</h3>
          <label className="footer-settings-label">
            Focus heading
            <input
              type="text"
              className="footer-settings-input"
              value={footer.focusHeading}
              onChange={(e) => handleChange('focusHeading', e.target.value)}
              placeholder="Our Focus"
            />
          </label>
          {[0, 1, 2].map((i) => (
            <label key={i} className="footer-settings-label">
              Focus item {i + 1}
              <input
                type="text"
                className="footer-settings-input"
                value={focusItems[i] || ''}
                onChange={(e) => handleFocusItemChange(i, e.target.value)}
                placeholder={
                  i === 0
                    ? 'Corporate Advisory'
                    : i === 1
                      ? 'Digital Platform Services'
                      : 'International & Regional Firms'
                }
              />
            </label>
          ))}
        </div>

        <div className="footer-settings-field footer-settings-field-wide">
          <h3 className="footer-settings-section-title">Contact</h3>
          <label className="footer-settings-label">
            Contact heading
            <input
              type="text"
              className="footer-settings-input"
              value={footer.contactHeading}
              onChange={(e) => handleChange('contactHeading', e.target.value)}
              placeholder="Contact"
            />
          </label>
          <label className="footer-settings-label">
            Address
            <input
              type="text"
              className="footer-settings-input"
              value={footer.address}
              onChange={(e) => handleChange('address', e.target.value)}
              placeholder="Sheikh Zaid, Giza, Egypt"
            />
          </label>
          <label className="footer-settings-label">
            Phone
            <input
              type="text"
              className="footer-settings-input"
              value={footer.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="01000355950"
            />
          </label>
          <label className="footer-settings-label">
            Email
            <input
              type="email"
              className="footer-settings-input"
              value={footer.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="Info@WhalesAdvisory.com"
            />
          </label>
        </div>

        <div className="footer-settings-field footer-settings-field-wide">
          <h3 className="footer-settings-section-title">Bottom bar</h3>
          <label className="footer-settings-label">
            Copyright text
            <input
              type="text"
              className="footer-settings-input"
              value={footer.copyrightText}
              onChange={(e) => handleChange('copyrightText', e.target.value)}
              placeholder="Copyright © 2026 Whales Advisory. All Rights Reserved."
            />
          </label>

          <div className="footer-settings-social-block">
            <div className="footer-settings-social-head">
              <span className="footer-settings-social-title">Social media</span>
              <button
                type="button"
                className="footer-settings-add-social"
                onClick={addSocialLink}
              >
                Add link
              </button>
            </div>
            <p className="footer-settings-social-hint">
              Only links with a URL are shown on the site. Leave the list empty to hide all icons.
            </p>
            {socialLinks.length === 0 && (
              <p className="footer-settings-social-empty">No social links yet. Click &quot;Add link&quot;.</p>
            )}
            {socialLinks.map((row, index) => (
              <div key={index} className="footer-social-row">
                <label className="footer-settings-label footer-social-platform">
                  Platform
                  <select
                    className="footer-settings-input"
                    value={row.platform || 'linkedin'}
                    onChange={(e) =>
                      handleSocialChange(index, 'platform', e.target.value)
                    }
                  >
                    {FOOTER_SOCIAL_PLATFORMS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="footer-settings-label footer-social-url">
                  URL
                  <input
                    type="url"
                    className="footer-settings-input"
                    value={row.url || ''}
                    onChange={(e) =>
                      handleSocialChange(index, 'url', e.target.value)
                    }
                    placeholder="https://..."
                  />
                </label>
                <button
                  type="button"
                  className="footer-social-remove"
                  onClick={() => removeSocialLink(index)}
                  aria-label="Remove social link"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {message && (
        <p className="footer-settings-message">{message}</p>
      )}
    </div>
  );
}

export default FooterSettings;
